import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CredentialsService } from '@app/auth/credentials.service';
import { AuthenticationService } from 'src/app/auth/authentication.service';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

@Injectable()
export class AuthenticatedInterceptor implements HttpInterceptor {
    private isRefreshing = false;

    constructor(
        private authenticationService: AuthenticationService,
        private router: Router,
        private credentialsService: CredentialsService
    ) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        // Public routes that don't require authentication
        const publicRoutes = [
            '/verify-email',
            '/reduction-authorization',
            '/vacation-request-authorization',
            '/notfound',
            '/auth/password-update/reset',
            '/auth/ceo-login',
            '/auth/ceo-access',
            '/personal-requisition-authorization',
            '/assessment-response',
            '/auth/login',
            '/auth'
        ];

        // Get the current URL and remove query parameters for checking
        const currentUrl = this.router.url.split('?')[0];

        const isPublicRoute = publicRoutes.some(route =>
            currentUrl.startsWith(route)
        );

        let enhancedRequest = request.clone({
            setHeaders: {
                /*  'Content-Type': 'application/json', */
                accept: '*/*',
            },
        });

        // Only add authorization token if:
        // 1. We have valid credentials with accessToken
        // 2. We're authenticated (token is not expired)
        // Note: For public routes, we still add the token if the user IS authenticated
        // This allows authenticated users to access public routes with their identity
        const credentials = this.credentialsService.credentials;
        const isAuthenticated = this.credentialsService.isAuthenticated();

        if (credentials?.accessToken && isAuthenticated) {
            enhancedRequest = enhancedRequest.clone({
                setHeaders: {
                    Authorization: `Bearer ${credentials.accessToken}`,
                },
            });
        }

        return next.handle(enhancedRequest).pipe(
            catchError((error) => {
                if (
                    error instanceof HttpErrorResponse &&
                    error.status === 401 &&
                    !request.url.includes('/login') &&
                    !request.url.includes('/token-refresh')
                ) {
                    // If we're on a public route, don't try to refresh or redirect
                    if (isPublicRoute) {
                        return throwError(() => error);
                    }

                    const credentials = this.credentialsService.credentials;
                    if (
                        credentials?.remember &&
                        credentials?.refreshToken &&
                        !this.isRefreshing
                    ) {
                        this.isRefreshing = true;

                        return this.authenticationService
                            .refreshToken({
                                refreshToken: credentials.refreshToken,
                            })
                            .pipe(
                                switchMap((newTokens) => {
                                    this.isRefreshing = false;

                                    const clonedRequest = request.clone({
                                        setHeaders: {
                                            Authorization: `Bearer ${this.credentialsService.credentials?.accessToken}`,
                                        },
                                    });

                                    return next.handle(clonedRequest);
                                }),
                                catchError((refreshError) => {
                                    this.isRefreshing = false;
                                    this.authenticationService.cleanCredentials();
                                    this.router.navigate(['/auth/login']);
                                    return throwError(() => refreshError);
                                })
                            );
                    } else {
                        // Only redirect to login if not on a public route
                        this.authenticationService.cleanCredentials();
                        this.router.navigate(['/auth/login']);
                    }
                }

                return throwError(() => error);
            })
        );
    }
}
